<template>
  <div>
    <div class="messageContainer" @mouseover="isHovered = true" @mouseleave="isHovered = false">
      <div v-if="message.mainContent" class="message" style="white-space: pre-line" :style="elementStyle">
        <div v-if="areDetailsShown" v-show="isHovered" class="logContainer" :style="elementStyle">
          <Logger
            :intent-analysed="intentAnalysed"
            :action-deduced="actionDeduced"
            :context="context"
            :intent-info="config"
            :is-hovered="isHovered" />
        </div>
        <!--eslint-disable vue/no-v-html-->
        <p v-html="formatMessage(displayedMainContent)" />
        <!--eslint-enable vue/no-v-html-->
      </div>
      <div v-if="message.images && message.images.length" class="messageImg">
        <div v-if="!message.mainContent && areDetailsShown" v-show="isHovered" class="logContainer">
          <Logger
            :intent-analysed="intentAnalysed"
            :action-deduced="actionDeduced"
            :context="context"
            :intent-info="config"
            :is-hovered="isHovered" />
        </div>
        <img
          v-for="(imageSrc, index) in messageImages"
          :key="index"
          v-lazy="imageSrc"
          @click.prevent="openSlide(index)"
          @load="scrollToBottom(message.isNewMessage)" />
        <div class="img-zoom-icon" @click.prevent="openSlide()">
          <i class="fa fa-search"></i>
        </div>
      </div>
      <div v-if="message.videos && message.videos.length" class="messageVideo">
        <div class="videoContainer">
          <div v-if="message.videos[0].cover && showCover" @click="playVideo">
            <i class="fas fa-play-circle" aria-hidden="true"></i>
            <img
              v-lazy="message.videos[0].cover"
              :src="message.videos[0].cover"
              @load="scrollToBottom(message.isNewMessage)" />
          </div>
          <video
            v-show="!showCover || !message.videos[0].cover"
            :id="videoId"
            :poster="message.videos[0].cover"
            controls
            @playing="updatePaused">
            <source v-for="(source, key) in message.videos[0].urls" :key="key" :src="source" />
            <!-- eslint-disable-next-line vue/no-v-html -->
            <p v-html="$t('video_unsupported_message', { videoSource: message.videos[0].urls[0] })"></p>
          </video>
        </div>
      </div>
      <LoggerLinks
        :intent-analysed="intentAnalysed"
        :action-deduced="actionDeduced"
        :are-details-shown="areDetailsShown"
        :assistant-id="assistantId"
        :assistant-slug="assistantSlug"
        @openUrl="openUrl" />
      <div v-show="isHovered" class="messageTime">{{ formatDate(message.sentTime, locale, $t) }}</div>
    </div>
  </div>
</template>

<script>
// Components
import Logger from './Logger'
import LoggerLinks from './LoggerLinks'

// Config
import config from '../../config/messages'
import defaultConfig from '../../config'

// Dependencies
import { mapGetters } from 'vuex'

// utils
import { mixin, formatDate, areDetailsShown, scrollToBottom } from '../../lib/utils'
import audioLib from '../../lib/audio'
import videoLib from '../../lib/video'

export default {
  name: 'Message',
  components: {
    Logger,
    LoggerLinks
  },
  mixins: [mixin],
  props: {
    message: { type: Object, required: true },
    intentAnalysed: { type: Object, default: null },
    context: { type: Object, default: null },
    actionDeduced: { type: Object, default: null },
    isShowLogs: { type: Boolean, default: false },
    elementStyle: { type: [Object, Boolean], default: null },
    assistantId: { type: String, required: true },
    assistantSlug: { type: String, required: true },
    streamingGateId: { type: [String, Number], default: null }
  },
  data: function () {
    return {
      config,
      isHovered: false,
      showCover: true,
      streamedContent: '',
      streamTimer: null,
      streamWordIndex: 0,
      streamTokens: [],
      streamDelay: 0,
      isStreamingActive: false,
      isStreamInit: false
    }
  },
  computed: {
    ...mapGetters({
      isTTSEnabled: 'isTTSEnabled',
      locale: 'locale'
    }),
    areDetailsShown: function () {
      return areDetailsShown(this.context, this.intentAnalysed, this.actionDeduced, this.isShowLogs)
    },
    videoId() {
      return `video_${this.message.sentTime}`
    },
    messageImages() {
      return this.message.imagesObj ? this.message.imagesObj.map(obj => obj.resizedImage) : this.message.image
    },
    displayedMainContent() {
      return this.isStreamingActive ? this.streamedContent : this.message.mainContent
    },
    streamId() {
      return this.message && (this.message._id || this.message.sentTime)
    }
  },
  watch: {
    streamId: function () {
      this.resetStreamingState()
      this.initStreaming()
    },
    streamingGateId: function () {
      this.initStreaming()
    }
  },
  mounted() {
    this.initStreaming()
  },
  beforeDestroy() {
    this.stopStreaming()
  },
  methods: {
    formatDate,
    scrollToBottom,
    resetStreamingState() {
      this.stopStreaming()
      this.streamedContent = ''
      this.isStreamInit = false
    },
    initStreaming() {
      if (!this.shouldStream() || this.isStreamInit) {
        return
      }

      const content = this.getStreamSource()
      const tokens = this.getStreamTokens(content)

      if (!tokens.length) {
        return
      }

      this.stopStreaming()
      this.isStreamInit = true
      this.streamTokens = tokens
      this.streamWordIndex = 0
      this.streamedContent = ''
      this.streamDelay = this.getStreamDelay(tokens.length)
      this.isStreamingActive = true
      this.$emit('stream-start', this.streamId)

      this.addTokenToStream()
    },
    shouldStream() {
      if (!this.message || !this.message.mainContent) {
        return false
      }

      if (this.message.isStreamingDisabled) {
        return false
      }

      if (this.message.isNewMessage !== true) {
        return false
      }

      // Streaming gate: only the message whose id matches the gate can stream.
      // This enforces sequential streaming (message 1, then 2, then 3...) even if all messages are rendered.
      return !!this.streamId && this.streamId === this.streamingGateId
    },
    getStreamSource() {
      return typeof this.message.mainContent === 'string' ? this.message.mainContent : String(this.message.mainContent)
    },
    getStreamTokens(content) {
      const tokens = content.match(/\S+\s*/g)
      return tokens || []
    },
    getStreamDelay(wordCount) {
      const baseDelay = defaultConfig.STREAMING.BASE_DELAY_MS

      if (!wordCount) {
        return baseDelay
      }

      // Target a base per-word delay, but cap the total streaming duration for long messages.
      // We compute: min(maxDuration, wordCount * baseDelay), then spread that duration across words.
      const duration = Math.min(defaultConfig.STREAMING.MAX_DURATION_MS, wordCount * baseDelay)
      // Enforce a minimum per-word delay so very short messages don't stream too fast.
      return Math.max(defaultConfig.STREAMING.MIN_DELAY_MS, Math.floor(duration / wordCount))
    },
    addTokenToStream() {
      if (!this.isStreamingActive) {
        return
      }

      const token = this.streamTokens[this.streamWordIndex]
      if (token) {
        this.streamedContent += token
        this.scrollToBottom(this.message.isNewMessage)
      }

      this.streamWordIndex += 1

      if (this.streamWordIndex >= this.streamTokens.length) {
        this.stopStreaming()
        this.streamedContent = this.getStreamSource()
        return
      }

      this.streamTimer = setTimeout(() => {
        this.addTokenToStream()
      }, this.streamDelay)
    },
    stopStreaming() {
      const wasStreaming = this.isStreamingActive
      if (this.streamTimer) {
        clearTimeout(this.streamTimer)
      }

      this.streamTimer = null
      this.isStreamingActive = false
      this.streamTokens = []
      this.streamWordIndex = 0

      if (wasStreaming) {
        this.$emit('stream-end', this.streamId)
      }
    },
    openUrl(url) {
      this.$emit('openUrl', url)
    },
    // Play the video triggered from click event on overlay icon
    playVideo() {
      this.showCover = false
      const video = document.getElementById(`video_${this.message.sentTime}`)
      if (video) {
        video.play()
      }
    },
    // Pause current playing audio/video when starting to play a video
    // event triggered on 'playing' event
    updatePaused(event) {
      // Stop audio
      audioLib.stop(this.isTTSEnabled)
      // Stop other playing video
      videoLib.pauseVideos(event.target)
    },
    openSlide(index = 0) {
      const mediaSources = []

      // The imagesObj will present for new messages, it will not present for older conversations, in that case message.images is used
      // imagesObj contains both raw image and resized image
      if (this.message.imagesObj && this.message.imagesObj.length) {
        mediaSources.push(...this.message.imagesObj.map(obj => obj.rawImage))
      } else if (this.message.images && this.message.images.length) {
        mediaSources.push(...this.message.images)
      }

      // @TODO: Add message videos after the issue get closed https://github.com/sachinchoolur/lg-video.js/issues/9
      // see https://trello.com/c/qcE39zcN/1048-webchat-video-lightbox

      if (!mediaSources.length) {
        return
      }

      parent.postMessage(
        {
          event: defaultConfig.FRAME_EVENT_TYPES.SENT.OPEN_LIGHT_GALLERY,
          index,
          mediaSources
        },
        '*'
      )
    }
  }
}
</script>
